import {
  BookingGutsFragment,
  BookingStatusQueryVariables,
  LocaleFragment,
  StripeCardFragment,
} from "../graphql/queries"
import * as moment from "moment"
import {createDateTime} from "./charges"
import {momentBookingDateToBackendString} from "./time"
import {BookingFormJourneyState} from "../state"
import {ID} from "./id"
import {AddBookingState} from "../components/AddBookingWizard"
import {emptyAirportState} from "../components/AirportStop"
import {emptyPassenger} from "./passenger/passenger"

export function babyOrBoosterSeat(booking: {
  babySeatsForward: number
  babySeatsBackward: number
  boosterSeats: number
}) {
  return booking.babySeatsForward > 0 || booking.babySeatsBackward > 0 || booking.boosterSeats > 0
}

export function bookingToBookingStatusVariables(booking: {
  bookingDate: string
  bookingHour: number
  bookingMinute: number
  journey: BookingFormJourneyState
}): BookingStatusQueryVariables {
  // We round the moment to 5 minute intervals *just* to save apollo client network calls...
  const now = moment()
  now
    .minute(Math.floor(now.minute() / 5) * 5)
    .second(0)
    .millisecond(0)

  return {
    bookingDateTime: createDateTime(
      booking.bookingDate,
      booking.bookingHour,
      booking.bookingMinute,
    ),
    originId: booking.journey.origin ? booking.journey.origin.id : null,
  }
}

function cardExpiryMoment(card: StripeCardFragment) {
  if (card.expiryMonth === null || card.expiryYear === null) {
    return null
  }
  const month = (card.expiryMonth < 10 ? "0" : "") + card.expiryMonth
  return moment(`${card.expiryYear}-${month}-01`).add(1, "months")
}

export function bookingCardHasExpired(
  stripeCard: StripeCardFragment | null,
  bookingDate: string | null,
) {
  if (!stripeCard || !bookingDate) {
    return false
  }
  // This is the instant the card expires
  const expiry = cardExpiryMoment(stripeCard)
  if (expiry === null) {
    return false
  }
  return moment(bookingDate).isSameOrAfter(expiry)
}

function repeatStripeCard(bookingBookerId: ID | null, stripeCard: StripeCardFragment | null) {
  // You can't reuse a third parties CC on a **new** booking.
  if (stripeCard && stripeCard.bookerId === bookingBookerId) {
    return stripeCard
  }
  return null
}

export function repeatBookingProperties(
  b: BookingGutsFragment,
  locale: LocaleFragment,
): AddBookingState {
  return {
    forBooker: b.booker,
    bookerFormOpen: false,
    details: {
      bookingDate: momentBookingDateToBackendString(
        moment.max(
          moment()
            .add(1, "days")
            .startOf("hour"),
          moment(b.bookingDate),
        ),
      ),
      bookingHour: b.bookingHour,
      bookingMinute: b.bookingMinute,
      passengerCount: b.passengerCount.toString(),
      babySeatsForward: b.babySeatsForward,
      babySeatsBackward: b.babySeatsBackward,
      boosterSeats: b.boosterSeats,
      wantPeopleMover: b.wantPeopleMover,
      journey: b.journey,
      airportPickup: b.airportPickup || emptyAirportState(),
      airportDropoff: b.airportDropoff || emptyAirportState(),
      passengers: b.passengers,
      editingDate: false,
      initialPassenger: emptyPassenger(),
      editingPassenger: false,
      editingPassengerIndex: null,
      locale,
    },
    stripeCard: {
      card: repeatStripeCard(b.booker && b.booker.id, b.stripeCard),
      loading: false,
    },
    paymentLink: {
      toEmail: b.paymentLink ? b.paymentLink.toEmail : null,
      editing: false,
    },
    promotion: {
      promoCode: b.promoCode,
      giftLoyaltyCredits: b.giftLoyaltyCredits,
    },
    notes: b.notes,
    paymentSectionOpen: true,
  }
}

export function returnBookingProperties(
  b: BookingGutsFragment,
  locale: LocaleFragment,
): AddBookingState {
  const base = repeatBookingProperties(b, locale)
  return {
    ...base,
    details: {
      ...base.details,
      airportPickup: base.details.airportDropoff,
      airportDropoff: base.details.airportPickup,
      journey: {
        origin: base.details.journey.destination,
        destination: base.details.journey.origin,
        waypoints: [...base.details.journey.waypoints].reverse(),
      },
    },
  }
}



// WEBPACK FOOTER //
// ./src/model/booking.ts